import { defineComponent, reactive, SetupContext } from '@vue/composition-api';
import MessageConst from '@/shared/message-const';
import HttpStatus from 'http-status-codes';                  // HTTPステータスコード取得用
import vueRouter from '@/router/index';                      // ルート情報取得

/**
 * 共通メッセージダイアログコンポーネント
 *
 * メッセージダイアログの表示制御及びエラー画面遷移制御を行う
 */
// reactiveDataの型定義
export type reactiveData = {
    isError: boolean;                                       // エラーメッセージかどうか
    displayMessage: string;                                 // 表示するメッセージ内容
    errorDetails: string[];                                 // エラーの詳細(ダイアログ表示のみ使用)
    showDialog: boolean;                                    // モーダル画面表示／非表示
    transitionUrl: string;                                  // 閉じるボタンを押下した後の遷移先URL
    isConfirmation: boolean;                                // 確認メッセージ表示有無フラグ
    isLoading: boolean;                                     // 親コンポーネント内の処理が終了したかどうかの判定フラグ
    windowSize: { x: number; y: number };                   // ウィンドウサイズ
};

export default defineComponent({
    setup(props, ctx: SetupContext) {
        const state = <reactiveData>reactive({
            isError: false,                                 // エラーメッセージかどうか
            displayMessage: '',                             // 表示するメッセージ内容
            errorDetails: [],                               // エラーの詳細(ダイアログ表示のみ使用)
            showDialog: false,                              // モーダル画面表示／非表示
            transitionUrl: '',                              // 閉じるボタンを押下した後の遷移先URL
            isConfirmation: false,                          // 確認メッセージ表示有無フラグ
            isLoading: false,                               // 親コンポーネント内の処理が終了したかどうかの判定フラグ
            windowSize: { x: 0, y: 0 }                      // ウィンドウサイズ
        });
        const router = vueRouter;                           // ルータ

        /**
         * エラー画面遷移の制御関数
         */
        function errorDisplayTransitionControl(response: any) {
            if (response.status === HttpStatus.BAD_REQUEST) {
                // 入力チェックに関するエラー(HTTPステータスコード:400)が発生した場合
                state.isLoading = false;
                state.isConfirmation = false;
                state.errorDetails = response.data.errors;
                state.isError = true;
                state.showDialog = true;
            } else if (response.status === HttpStatus.NOT_FOUND) {
                // 存在チェックのエラー(HTTPステータスコード:404)が発生した場合
                state.isLoading = false;
                state.isConfirmation = false;
                state.errorDetails = [response.data.message];
                state.isError = true;
                state.showDialog = true;
            } else {
                // 続行不可能なサーバ上のエラー(HTTPステータスコード:401, 500など)が発生した場合
                if (response.data.message) {
                    ctx.root.$store.commit('setErrorMessage', response.data.message);
                } else {
                    // メッセージが設定されていない場合
                    ctx.root.$store.commit('setErrorMessage', MessageConst.E_SYSTEM_ERROR);
                }
                router.push('/error');
            }
        }

        /**
         * メッセージダイアログ表示関数
         * @param message 表示メッセージ
         */
        function simpleDisplayMessageDialog(message: string) {
            state.isLoading = false;                         // 処理実行終了
            state.isConfirmation = false;                    // メッセージダイアログとして表示
            state.displayMessage = message;                  // 表示メッセージ
            state.isError = false;                           // 正常メッセージ
            state.showDialog = true;                         // ダイアログ表示
        }

        /**
         * メッセージダイアログ表示関数
         */
        function displayMessageDialog(response: any) {
            state.isLoading = false;                         // 処理実行終了
            state.isConfirmation = false;                    // メッセージダイアログとして表示
            state.displayMessage = response.data.message;    // 表示メッセージ
            state.isError = false;                           // 正常メッセージ
            state.showDialog = true;                         // ダイアログ表示
        }

        /**
         * メッセージダイアログ表示関数(画面遷移先指定)
         */
        async function displayMessageTransitionDialog(response: any, url: string) {
            state.isLoading = false;                         // 処理実行終了
            state.isConfirmation = false;                    // メッセージダイアログとして表示
            state.transitionUrl = url;                       // 遷移先URL
            state.displayMessage = response.data.message;    // 表示メッセージ
            state.isError = false;                           // 正常メッセージ
            state.showDialog = true;                         // ダイアログ表示
        }
        
        /**
         * エラーメッセージダイアログ表示関数(画面遷移先指定)
         */
        function displayErrorMessageTransitionDialog(response: any, url: string) {
            state.isLoading = false;                         // 処理実行終了
            state.isConfirmation = false;                    // メッセージダイアログとして表示
            state.transitionUrl = url;                       // 遷移先URL
            state.errorDetails = [response.data.message];    // 表示メッセージ
            state.isError = true;                            // 正常メッセージ
            state.showDialog = true;                         // ダイアログ表示
        }

        /**
         * エラー用メッセージダイアログ表示関数
         * ダイアログ表示後、遷移元の画面に遷移する
         */
        async function errorDisplayMessageTransitionDialog(response: any) {
            state.isLoading = false;                                             // 処理実行終了
            state.isConfirmation = false;                                        // メッセージダイアログとして表示
            state.transitionUrl = ctx.root.$store.getters.getBeforeDisplayPath;  // 遷移先URL
            state.errorDetails = [response.data.message];                        // 表示メッセージ
            state.isError = true;                                                // エラーメッセージ
            state.showDialog = true;                                             // ダイアログ表示
        }

        /**
         * 確認メッセージダイアログ表示関数
         */
        async function displayConfirmationMessageDialog(displayMessage: string) {
            state.isLoading = false;                         // 処理実行終了
            state.isConfirmation = true;                     // 確認メッセージダイアログとして表示
            state.displayMessage = displayMessage;           // 表示メッセージ
            state.isError = false;                           // 正常メッセージ
            state.errorDetails = [];                         // エラーの詳細をクリア
            state.showDialog = true;                         // ダイアログ表示
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 親コンポーネント側の関数イベントを発火させる
         */
        function callParentComponentMethod() {
            // 処理実行中
            state.isLoading = true;

            // 関数イベント発火
            ctx.emit('yesClick');
        }

        /**
         * モーダル画面を閉じる
         */
        function close(): void {
            // 処理実行終了
            state.isLoading = false;

            if (state.transitionUrl === '') {
                // 画面遷移なし
                state.showDialog = false;
            } else {
                // 画面遷移あり
                state.showDialog = false;
                router.push(state.transitionUrl);
            }
        }

        return {
            state,
            router,
            errorDisplayTransitionControl,
            simpleDisplayMessageDialog,
            displayMessageDialog,
            displayMessageTransitionDialog,
            displayErrorMessageTransitionDialog,
            errorDisplayMessageTransitionDialog,
            displayConfirmationMessageDialog,
            onResize,
            callParentComponentMethod,
            close
        };
    }
});
